import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/noto-sans-malayalam/400.css';
import '@fontsource/noto-sans-malayalam/600.css';
import './styles/app.css';
import './i18n';
import { ApiError, shouldRetry } from './api';
import { RequireAuth } from './components/RequireAuth';
import { Shell } from './components/Shell';
import { Dashboard } from './pages/Dashboard';
import { Announcements, Approvals, Notifications } from './pages/Announcements';
import { CertIssued, CertRequests, VerifyCertificate } from './pages/Certificates';
import { DataHealth } from './pages/DataHealth';
import { AppSettingsPage, AuditLogPage, MahalluProfilePage } from './pages/MahalluSettings';
import { DeliveryLog } from './pages/DeliveryLog';
import { Collect, Dues, PlansFunds, Receipts } from './pages/Money';
import { ReceiptPrint } from './pages/ReceiptPrint';
import { MemberManagement } from './pages/MemberManagement';
import { NotBuilt } from './pages/NotBuilt';
import { PrivacyRequests } from './pages/PrivacyRequests';
import { PrivacySettings } from './pages/PrivacySettings';
import { PublicPrivacy } from './pages/PublicPrivacy';
import { SequenceMaster } from './pages/SequenceMaster';
import { Sessions } from './pages/Sessions';
import { UsersRoles } from './pages/UsersRoles';
import { Wards } from './pages/Wards';
import { SignIn } from './pages/SignIn';
import { ConsoleActivityPage, ConsoleMahallusPage, ConsoleOperatorsPage, ConsoleOverviewPage, ConsoleRequestsPage, ConsoleShell, RequireConsole } from './pages/Console';
import { onConsoleHost } from './mahalluLink';

// console.rabitaone.in opens the OptiFi console (ADR-0040); locally the console lives under /console.
if (onConsoleHost() && !window.location.pathname.startsWith('/console')) window.history.replaceState(null, '', '/console');

// A session can end while a screen is open (expiry, sign-out elsewhere, role removed). Any 401 on a
// data request rechecks who is signed in; the sign-in gate then sends the person to sign in.
const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const signedOut = error instanceof ApiError && (error.status === 401 || error.code === 'CHOOSE_MAHALLU');
      if (signedOut && query.queryKey[0] !== 'me') void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  }),
  defaultOptions: { queries: { staleTime: 30_000, retry: shouldRetry, refetchOnWindowFocus: false } },
});

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignIn /> },
  { path: '/console/sign-in', element: <SignIn mode="console" /> },
  {
    path: '/console',
    element: (
      <RequireConsole>
        <ConsoleShell />
      </RequireConsole>
    ),
    children: [
      { index: true, element: <ConsoleOverviewPage /> },
      { path: 'mahallus', element: <ConsoleMahallusPage /> },
      { path: 'requests', element: <ConsoleRequestsPage /> },
      { path: 'operators', element: <ConsoleOperatorsPage /> },
      { path: 'activity', element: <ConsoleActivityPage /> },
    ],
  },
  { path: '/privacy/:slug', element: <PublicPrivacy /> },
  { path: '/verify/:token', element: <VerifyCertificate /> },
  {
    path: '/receipts/:id/print',
    element: (
      <RequireAuth>
        <ReceiptPrint />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: 'register/members', element: <MemberManagement /> },
      { path: 'settings/sequences', element: <SequenceMaster /> },
      { path: 'settings/users', element: <UsersRoles /> },
      { path: 'settings/privacy', element: <PrivacySettings /> },
      { path: 'register/privacy-requests', element: <PrivacyRequests /> },
      { path: 'register/data-health', element: <DataHealth /> },
      { path: 'varisankhya/dues', element: <Dues /> },
      { path: 'varisankhya/collect', element: <Collect /> },
      { path: 'varisankhya/receipts', element: <Receipts /> },
      { path: 'varisankhya/plans', element: <PlansFunds /> },
      { path: 'communication/delivery', element: <DeliveryLog /> },
      { path: 'certificates/requests', element: <CertRequests /> },
      { path: 'certificates/issued', element: <CertIssued /> },
      { path: 'communication/announcements', element: <Announcements /> },
      { path: 'communication/send', element: <Announcements /> },
      { path: 'notifications', element: <Notifications /> },
      { path: 'approvals', element: <Approvals /> },
      { path: 'settings/profile', element: <MahalluProfilePage /> },
      { path: 'settings/app', element: <AppSettingsPage /> },
      { path: 'settings/audit', element: <AuditLogPage /> },
      { path: 'account/sessions', element: <Sessions /> },
      { path: 'register/wards', element: <Wards /> },
      { path: 'soon/:key', element: <NotBuilt /> },
      { path: '*', element: <NotBuilt /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
